import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TOOLS } from '../data/tools';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { trackEvent } from '../utils/analytics';
import { ToolHero } from '../components/tools/ToolHero';
import { ToolExplainer } from '../components/tools/ToolExplainer';
import { ToolDemo } from '../components/tools/ToolDemo';
import { ToolHowItWorks } from '../components/tools/ToolHowItWorks';
import { ToolOutputs } from '../components/tools/ToolOutputs';
import { ToolProcessFit } from '../components/tools/ToolProcessFit';
import { ToolFaq } from '../components/tools/ToolFaq';
import { ToolFinalCta } from '../components/tools/ToolFinalCta';
import { RelatedTools } from '../components/tools/RelatedTools';
import { NotFound } from './NotFound';
import type { Tool } from '../types/tools';
type ToolPageProps = {
  showIllustrativeCounts: boolean;
};
export function ToolPage({
  showIllustrativeCounts
}: ToolPageProps) {
  const {
    slug
  } = useParams();
  const tool = TOOLS.find(item => item.slug === slug);
  if (!tool) return <NotFound data-id="element-618" data-mp="30goiubddxob5" />;
  return <ToolPageContent key={tool.slug} tool={tool} showIllustrativeCounts={showIllustrativeCounts} data-id="element-619" data-mp="30h7q8y5esuax" />;
}
function ToolPageContent({
  tool,
  showIllustrativeCounts
}: {
  tool: Tool;
  showIllustrativeCounts: boolean;
}) {
  const path = `/tools/${tool.slug}`;
  const url = canonicalUrl(path);
  usePageMeta({
    title: tool.seo.title,
    description: tool.seo.description,
    path,
    keywords: tool.seo.keywords,
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Tools',
        item: canonicalUrl('/tools')
      }, {
        '@type': 'ListItem',
        position: 3,
        name: tool.name,
        item: url
      }]
    }, {
      '@type': 'SoftwareApplication',
      name: `DiligenceGPT™ ${tool.name}`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url,
      description: tool.hero.supporting,
      publisher: {
        '@id': 'https://www.startupfuel.com/#organization'
      }
    }]
  });
  useEffect(() => {
    trackEvent('tool_page_view', {
      tool: tool.slug
    });
  }, [tool.slug]);
  return <main id="main" data-id="element-620" data-mp="gnz0nqnvr6ha">
      <ToolHero tool={tool} showIllustrativeCounts={showIllustrativeCounts} data-id="element-621" data-mp="gn5yz43s3760" />
      <ToolExplainer tool={tool} data-id="element-622" data-mp="3f6cwkzpcg1no" />
      <ToolDemo tool={tool} data-id="element-623" data-mp="3f75tza2exet5" />
      <ToolHowItWorks tool={tool} data-id="element-624" data-mp="3k4egtnyia70q" />
      <ToolOutputs tool={tool} data-id="element-625" data-mp="3k5h31ikvvlab" />
      <ToolProcessFit tool={tool} data-id="element-626" data-mp="3k4o3b1kcdw1o" />
      <ToolFaq tool={tool} data-id="element-627" data-mp="3p3qyluaisnwg" />
      <ToolFinalCta tool={tool} data-id="element-628" data-mp="3p2fvzxst9m6x" />
      <RelatedTools tool={tool} data-id="element-629" data-mp="3p5ln1il02y5a" />
    </main>;
}